import { useCallback, useContext, useEffect, useState } from "react";
import ErrorMessage from "../../shared/components/Error";
import Loading from "../../shared/components/Loading";
import FloatingPanelService from "../../shared/FloatingPanel";
import { TranslationService } from "../../../services/TranslationService";
// import ToastContext from "../../shared/bootstrap/Toast";
// import ModalService from "../../shared/bootstrap/Modal";
import {
  RequiredManager,
  ValidationMessage
} from "../../shared/RequieredManager";
import { CheckBoxEditor, TextEditor } from "../../shared/components/Editors";
import { ActivityTypeListResponse } from "../../../entities/company/ActivityType/ActivityTypeListResponse";
import ActivityTypeService from "../../../services/ActivityTypeService";
import { ActivityTypeEntity } from "../../../entities/company/ActivityType/ActivityTypeEntity";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import ModalService from "../../shared/bootstrap/modal/Modal";
import ToastContext from "../../shared/bootstrap/Toast";
import CompanyService from "../../../services/CompanyService";

const ActivityType = () => {
  const { translate } = TranslationService;
  const [response, setResponse] = useState<ActivityTypeListResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const requestData = async () => {
    setLoading(true);
    const result = await ActivityTypeService.getList();
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      return;
    }
    setResponse(result);
    setLoading(false);
    setError(false);
  };

  const requestDataCallback = useCallback(requestData, []);

  useEffect(() => {
    requestDataCallback();
  }, [requestDataCallback]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <ErrorMessage
        onRefresh={requestDataCallback}
        message={translate.ErrorLoadingList}
      />
    );
  }

  const showActivityTypeEdit = (activityType?: ActivityTypeEntity) => {
    FloatingPanelService.showPanel({
      children: (
        <ActivityTypeEdit
          reload={requestDataCallback}
          activityType={activityType}
          readonly={!CompanyService.canDo("editactivitytype")}
        />
      ),
      title: activityType
        ? translate.EditActivityType
        : translate.NewActivityType,
      width: 600,
      height: 500,
      position: "center"
    });
  };

  return (
    <div className="main-layout-container mx-3">
      <div className="main-panel">
        <h4 className="mb-2">{TranslationService.translate.ActivitiesTypes}</h4>
        <div className="card">
          <div className="d-flex align-items-center justify-content-end mb-2">
            {CompanyService.canDo("editactivitytype") && (
              <button
                className="btn btn-primary"
                onClick={() => showActivityTypeEdit(undefined)}
              >
                <i className="fal fa-plus" /> {translate.NewActivityType}
              </button>
            )}
          </div>
          <div className="tableContainer overflow-x-auto">
            <table className="table genericTable table-ellipsis">
              <thead>
                <tr>
                  <th>{translate.Value}</th>
                  <th className="text-center">{translate.EfectiveActivity}</th>
                  <th className="text-center">{translate.Mails}</th>
                  <th className="text-center">{translate.DefaultOption}</th>
                  <th className="text-center">{translate.Comments}</th>
                  <th className="text-center">{translate.DefaultOption}</th>
                  <th className="text-center">{translate.Tasks}</th>
                  <th className="text-center">{translate.DefaultOption}</th>
                  <th className="text-center">{translate.SMS}</th>
                  <th className="text-center">{translate.DefaultOption}</th>
                </tr>
              </thead>
              <tbody>
                {response?.list.map((x) => (
                  <tr
                    key={x.ActivityTypeID}
                    className="pointer"
                    onClick={(event) => {
                      showActivityTypeEdit(x);
                      event.stopPropagation();
                    }}
                  >
                    <td className="text-start">{x.ActivityTypeName}</td>
                    <td className="text-center">
                      {x.Effective && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.Mail && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.MailDefault && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.Comment && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.CommentDefault && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.Task && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.TaskDefault && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.SMS && <i className="fa fa-check"></i>}
                    </td>
                    <td className="text-center">
                      {x.SMSDefault && <i className="fa fa-check"></i>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActivityTypeEdit = ({
  reload,
  activityType,
  readonly = false
}: {
  reload: () => void;
  activityType?: ActivityTypeEntity;
  readonly?: boolean | undefined;
}) => {
  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);
  const [submiting, setSubmiting] = useState<string | null>(null);
  const [activityTypeModel, setActivityTypeModel] =
    useState<ActivityTypeEntity>({
      ...(activityType ?? {
        ActivityTypeID: 0,
        ActivityTypeName: "",
        Comment: false,
        CommentDefault: false,
        Effective: false,
        Mail: false,
        MailDefault: false,
        SMS: false,
        SMSDefault: false,
        Task: false,
        TaskDefault: false
      })
    });
  const [name, setName] = useState(activityType?.ActivityTypeName ?? "");

  const requiredManager = new RequiredManager();
  const onValueChanged = requiredManager.makeRequired(setName);

  const onSubmit = async () => {
    if (submiting !== null) {
      return;
    }
    if (!requiredManager.validate()) {
      showToast(translate.MissingRequiredFields);
      return;
    }
    setSubmiting("set");
    activityTypeModel.ActivityTypeName = name;
    const result = await ActivityTypeService.set(activityTypeModel);
    if (result instanceof Error) {
      showToast(translate.ErrorProcessingRequest, undefined, "danger");
      setSubmiting(null);
      return;
    }
    setSubmiting(null);
    reload();
    FloatingPanelService.hidePanel();
    showToast(translate.ActivityTypeSaved, undefined, "success");
  };

  const onDelete = () => {
    const deleteTransactionType = async () => {
      if (submiting !== null) {
        return;
      }
      setSubmiting("del");
      const result = await ActivityTypeService.delete(
        activityType?.ActivityTypeID!
      );
      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
        setSubmiting(null);
        return;
      }
      setSubmiting(null);
      reload();
      FloatingPanelService.hidePanel();
      showToast(translate.ActivityTypeDeleted, undefined, "success");
    };
    ModalService.showDefaultModal({
      acceptButtonLabel: translate.Delete,
      message: () => (
        <>
          {translate.AreYouSureDeleteActivityType.split("\n").map((x) => (
            <>
              <span>{x}</span>
              <br />
            </>
          ))}
        </>
      ),
      onAcceptClick: deleteTransactionType
    });
  };

  return (
    <>
      <div className="floatingBody p-4">
        <div className="card-body">
          <div className="d-flex flex-column">
            <div className="row mb-3">
              <label className="col-3 font-weight-normal col-form-label">
                {translate.Name}:
              </label>
              <div className="col-8">
                <TextEditor
                  onChange={onValueChanged}
                  defaultValue={name}
                  disabled={readonly}
                />
                {
                  <ValidationMessage
                    onChange={onValueChanged}
                    defaultValue={name}
                  />
                }
              </div>
            </div>

            <div className="row">
              <div className="col-6">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({ ...activityTypeModel, Effective: x })
                  }
                  defaultValue={activityType?.Effective}
                  message={translate.EfectiveActivity}
                  disabled={readonly}
                ></CheckBoxEditor>
              </div>
            </div>
            <hr></hr>
            <div className="row">
              <div className="col-4">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      Mail: x,
                      MailDefault: !x ? false : activityTypeModel.MailDefault
                    })
                  }
                  value={activityTypeModel.Mail}
                  message={translate.Mails}
                  disabled={readonly}
                ></CheckBoxEditor>
              </div>
              <div className="col-6">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      MailDefault: x
                    })
                  }
                  value={activityTypeModel.MailDefault}
                  message={translate.DefaultOption}
                  disabled={!activityTypeModel.Mail || readonly}
                ></CheckBoxEditor>
              </div>
            </div>
            <div className="row">
              <div className="col-4">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      Comment: x,
                      CommentDefault: !x
                        ? false
                        : activityTypeModel.CommentDefault
                    })
                  }
                  value={activityTypeModel.Comment}
                  message={translate.Comments}
                  disabled={readonly}
                ></CheckBoxEditor>
              </div>
              <div className="col-6">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      CommentDefault: x
                    })
                  }
                  value={activityTypeModel.CommentDefault}
                  message={translate.DefaultOption}
                  disabled={!activityTypeModel.Comment || readonly}
                ></CheckBoxEditor>
              </div>
            </div>
            <div className="row">
              <div className="col-4">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      Task: x,
                      TaskDefault: !x ? false : activityTypeModel.TaskDefault
                    })
                  }
                  value={activityTypeModel.Task}
                  message={translate.Tasks}
                  disabled={readonly}
                ></CheckBoxEditor>
              </div>
              <div className="col-6">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      TaskDefault: x
                    })
                  }
                  value={activityTypeModel.TaskDefault}
                  message={translate.DefaultOption}
                  disabled={!activityTypeModel.Task || readonly}
                ></CheckBoxEditor>
              </div>
            </div>
            <div className="row">
              <div className="col-4">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      SMS: x,
                      SMSDefault: !x ? false : activityTypeModel.SMSDefault
                    })
                  }
                  value={activityTypeModel.SMS}
                  message={translate.SMS}
                  disabled={readonly}
                ></CheckBoxEditor>
              </div>
              <div className="col-6">
                <CheckBoxEditor
                  onChange={(x) =>
                    setActivityTypeModel({
                      ...activityTypeModel,
                      SMSDefault: x
                    })
                  }
                  value={activityTypeModel.SMSDefault}
                  message={translate.DefaultOption}
                  disabled={!activityTypeModel.SMS || readonly}
                ></CheckBoxEditor>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingPanelFooter>
        {activityType && CompanyService.canDo("editactivitytype") && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onDelete}
          >
            {translate.Delete}
            {submiting === "del" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}

        {CompanyService.canDo("editactivitytype") && (
          <button className="btn btn-primary btn-sm" onClick={onSubmit}>
            {translate.Save}
            {submiting === "set" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}
      </FloatingPanelFooter>
    </>
  );
};

export default ActivityType;
